import { useState, useMemo, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
// react-leaflet-cluster imports MarkerCluster.css / MarkerCluster.Default.css
// itself, so do not import them here as well - it ships the stylesheet twice.

import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PartnerMapPopup } from "@/components/network-map/PartnerMapPopup";
import { UnmappedPartnersSheet } from "@/components/network-map/UnmappedPartnersSheet";
import {
  createPartnerIcon,
  PARTNER_TYPE_COLORS,
} from "@/components/network-map/partnerMarkers";

import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/useRoles";
import { useAllChannelPartners } from "@/hooks/useAllChannelPartners";
import {
  Connector,
  ConnectorType,
  CONNECTOR_TYPE_LABELS,
  Permission,
} from "@/types";
import { searchItems } from "@/lib/searchUtils";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Search,
  RefreshCw,
  MapPinOff,
  Crosshair,
  ShieldAlert,
  Loader2,
} from "lucide-react";

// Same defaults as MapLocationPicker so both maps open on the same view of India.
const INDIA_CENTER: [number, number] = [20.5937, 78.9629];
const INDIA_ZOOM = 5;

type TypeFilter = "all" | ConnectorType;

const hasValidCoords = (partner: Connector): boolean =>
  !!partner.location &&
  Number.isFinite(partner.location.lat) &&
  Number.isFinite(partner.location.lng);

/**
 * Fits the map to the current pin set. Keyed on the pin IDs so it refits when
 * filters change, but not on every render while the admin pans around.
 */
function FitBoundsToPins({
  pins,
  refitToken,
}: {
  pins: Connector[];
  refitToken: number;
}) {
  const map = useMap();
  const boundsKey = pins.map((p) => p.id).join(",");

  useEffect(() => {
    if (pins.length === 0) {
      map.setView(INDIA_CENTER, INDIA_ZOOM);
      return;
    }

    const bounds = L.latLngBounds(
      pins.map((p) => [p.location!.lat, p.location!.lng] as [number, number]),
    );
    // maxZoom stops a lone pin from slamming the view down to street level.
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, refitToken, map]);

  return null;
}

export default function NetworkMapPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions(
    user?.roles || [],
  );
  const { partners, loading, errorCode, refresh } = useAllChannelPartners();

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);
  const [refitToken, setRefitToken] = useState(0);

  const filtered = useMemo(() => {
    const searchResults = searchItems(searchQuery, partners, [
      "name",
      "businessName",
      "city",
      "state",
      "pincode",
      "address",
      "whatsappNumber",
      "type",
    ]);

    return searchResults.filter((partner) => {
      if (!includeDeactivated && partner.isActive === false) return false;
      if (typeFilter !== "all" && partner.type !== typeFilter) return false;
      return true;
    });
  }, [partners, searchQuery, typeFilter, includeDeactivated]);

  const mapped = useMemo(() => filtered.filter(hasValidCoords), [filtered]);
  const unmapped = useMemo(
    () => filtered.filter((p) => !hasValidCoords(p)),
    [filtered],
  );

  const stats = useMemo(() => {
    const cities = new Set(
      mapped.map((p) => p.city?.trim()).filter(Boolean) as string[],
    );
    const states = new Set(
      mapped.map((p) => p.state?.trim()).filter(Boolean) as string[],
    );
    const pct =
      filtered.length > 0
        ? Math.round((mapped.length / filtered.length) * 100)
        : 0;
    return { cities: cities.size, states: states.size, pct };
  }, [mapped, filtered.length]);

  // Only show legend entries for types actually on the map, so it stays short.
  const legendTypes = useMemo(() => {
    const present = new Set(mapped.map((p) => p.type));
    return (Object.keys(PARTNER_TYPE_COLORS) as ConnectorType[]).filter((t) =>
      present.has(t),
    );
  }, [mapped]);

  // Belt-and-braces permission check on top of the route's PermissionGuard,
  // so the page never flashes content before roles resolve.
  if (permissionsLoading) {
    return (
      <AppLayout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (user && !hasPermission(Permission.ACCESS_ADMIN_TOOLS)) {
    return (
      <AppLayout>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">
            You don&apos;t have permission to access this page.
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 pt-6 max-w-7xl mx-auto space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-8 text-muted-foreground"
          onClick={() => navigate("/tools")}
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Tools
        </Button>

        <PageHeader
          title="Network Map"
          subtitle="Where your channel partner network actually is"
          action={
            <Button
              variant="outline"
              size="sm"
              className="h-9 flex-shrink-0"
              onClick={() => void refresh()}
              disabled={loading}
            >
              <RefreshCw
                className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")}
              />
              Refresh
            </Button>
          }
        />

        {errorCode === "permission-denied" ? (
          <Card>
            <CardContent className="p-6 flex flex-col items-center text-center gap-3">
              <ShieldAlert className="h-10 w-10 text-destructive" />
              <div>
                <p className="font-medium">Cannot read the partner network</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  Your role can open this tool, but it isn&apos;t permitted to
                  read every channel partner. Ask an admin to grant your account
                  the admin, business head, or operations role.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Coverage stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs text-muted-foreground">Partners</p>
                  <p className="text-xl md:text-2xl font-bold">
                    {filtered.length}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs text-muted-foreground">Mapped</p>
                  <p className="text-xl md:text-2xl font-bold">
                    {mapped.length}
                    <span className="text-sm font-normal text-muted-foreground">
                      {" / "}
                      {filtered.length}
                    </span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {stats.pct}% have a location
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs text-muted-foreground">Cities</p>
                  <p className="text-xl md:text-2xl font-bold">
                    {stats.cities}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 md:p-4">
                  <p className="text-xs text-muted-foreground">States</p>
                  <p className="text-xl md:text-2xl font-bold">
                    {stats.states}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search name, business, city, pincode..."
                    className="pl-10 h-9"
                  />
                </div>

                <Select
                  value={typeFilter}
                  onValueChange={(v) => setTypeFilter(v as TypeFilter)}
                >
                  <SelectTrigger className="h-9 md:w-48">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {(Object.keys(PARTNER_TYPE_COLORS) as ConnectorType[]).map(
                      (type) => (
                        <SelectItem key={type} value={type}>
                          {CONNECTOR_TYPE_LABELS[type]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Switch
                    id="include-deactivated"
                    checked={includeDeactivated}
                    onCheckedChange={setIncludeDeactivated}
                  />
                  <Label
                    htmlFor="include-deactivated"
                    className="text-xs whitespace-nowrap cursor-pointer"
                  >
                    Include deactivated
                  </Label>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 flex-shrink-0"
                  onClick={() => setUnmappedOpen(true)}
                >
                  <MapPinOff className="h-4 w-4 mr-1.5" />
                  Unmapped ({unmapped.length})
                </Button>
              </CardContent>
            </Card>

            {/* Map — Leaflet needs a resolved height or it renders 0px tall */}
            <div className="relative h-[60dvh] md:h-[70dvh] rounded-lg overflow-hidden border">
              <MapContainer
                center={INDIA_CENTER}
                zoom={INDIA_ZOOM}
                scrollWheelZoom
                className="h-full w-full"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
                  {mapped.map((partner) => (
                    <Marker
                      key={partner.id}
                      position={[partner.location!.lat, partner.location!.lng]}
                      icon={createPartnerIcon(partner.type, partner.isFavorite)}
                    >
                      <Popup>
                        <PartnerMapPopup partner={partner} />
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
                <FitBoundsToPins pins={mapped} refitToken={refitToken} />
              </MapContainer>

              {/* Overlays sit above Leaflet's panes, which top out at z-index 800 */}
              <Button
                variant="secondary"
                size="sm"
                className="absolute bottom-3 right-3 z-[900] h-9 shadow-lg bg-white hover:bg-gray-100 text-gray-800"
                onClick={() => setRefitToken((t) => t + 1)}
              >
                <Crosshair className="h-4 w-4 mr-1.5" />
                Reset view
              </Button>

              {loading && (
                <div className="absolute inset-0 z-[900] bg-background/70 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {!loading && mapped.length === 0 && (
                <div className="absolute inset-0 z-[900] bg-background/80 flex items-center justify-center p-6">
                  <div className="text-center max-w-sm">
                    <MapPinOff className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">No pins to show</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {filtered.length === 0
                        ? "No partners match your current filters."
                        : `${filtered.length} partner(s) match, but none has a geo-location saved yet. Open a partner and set its location to put it on the map.`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Legend */}
            {legendTypes.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                {legendTypes.map((type) => (
                  <span key={type} className="flex items-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PARTNER_TYPE_COLORS[type] }}
                    />
                    {CONNECTOR_TYPE_LABELS[type]}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <UnmappedPartnersSheet
        open={unmappedOpen}
        onOpenChange={setUnmappedOpen}
        partners={unmapped}
      />
    </AppLayout>
  );
}
